import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Shield, UserPlus, Trash2, Loader2, ChevronDown, Search, KeyRound, Mail, Copy, CheckCircle, MoreVertical, X } from 'lucide-react';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/40', desc: 'Full access — manage users, all projects, settings' },
  manager: { label: 'Manager', color: 'text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-950/30 border-cyan-200 dark:border-cyan-800/40', desc: 'Create & manage own projects, invite team, run audits' },
  member: { label: 'Member', color: 'text-muted-foreground bg-muted border-border', desc: 'View & contribute to projects they are invited to' },
};

function TempPasswordModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copyPassword = () => {
    navigator.clipboard.writeText(data.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-card border border-border rounded-sm p-6 w-full max-w-md" onClick={e => e.stopPropagation()}
        data-testid="temp-password-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-heading uppercase tracking-wider text-foreground flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-primary" />
            {data.type === 'reset' ? 'Password Reset' : 'Invite Sent'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground/80" data-testid="close-temp-password-modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-muted-foreground mb-3">
          {data.type === 'reset'
            ? `Password has been reset for ${data.email}`
            : `Account created and welcome email sent to ${data.email}`
          }
        </p>

        {data.email_sent ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/40 text-xs mb-4"
            data-testid="email-sent-success">
            <Mail className="w-3.5 h-3.5" /> Email sent successfully
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800/40 text-xs mb-4"
            data-testid="email-sent-fallback">
            <Mail className="w-3.5 h-3.5" /> Email not sent — share the password manually
          </div>
        )}

        <div className="mb-4">
          <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Temporary Password</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2.5 bg-muted border border-border rounded-sm text-primary font-mono text-sm tracking-wider"
              data-testid="temp-password-display">
              {data.temp_password}
            </code>
            <button onClick={copyPassword}
              data-testid="copy-temp-password-btn"
              className="px-3 py-2.5 border border-border rounded-sm text-muted-foreground hover:text-primary hover:border-primary/30 transition-colors">
              {copied ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground">The user should change this after signing in.</p>
      </div>
    </div>
  );
}

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', name: '', role: 'member' });
  const [inviting, setInviting] = useState(false);
  const [roleDropdown, setRoleDropdown] = useState(null);
  const [actionsDropdown, setActionsDropdown] = useState(null);
  const [tempPasswordData, setTempPasswordData] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-dropdown]')) {
        setRoleDropdown(null);
        setActionsDropdown(null);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const updateRole = async (userId, newRole) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update role'); }
    setRoleDropdown(null);
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    setActionsDropdown(null);
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete user'); }
  };

  const resetPassword = async (userId) => {
    setActionLoading(userId);
    setActionsDropdown(null);
    try {
      const res = await api.post(`/admin/users/${userId}/reset-password`);
      setTempPasswordData({ ...res.data, type: 'reset' });
    } catch (err) { alert(err.response?.data?.detail || 'Failed to reset password'); }
    finally { setActionLoading(null); }
  };

  const resendInvite = async (userId) => {
    setActionLoading(userId);
    setActionsDropdown(null);
    try {
      const res = await api.post(`/admin/users/${userId}/resend-invite`);
      setTempPasswordData({ ...res.data, type: 'invite' });
    } catch (err) { alert(err.response?.data?.detail || 'Failed to resend invite'); }
    finally { setActionLoading(null); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await api.post('/admin/users/invite', inviteData);
      setUsers(prev => [{ ...res.data, created_at: new Date().toISOString(), last_login: null }, ...prev]);
      setShowInvite(false);
      setInviteData({ email: '', name: '', role: 'member' });
      // Show the confirmation modal with email status + temp password
      setTempPasswordData({ ...res.data, type: 'invite' });
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create user'); }
    finally { setInviting(false); }
  };

  const filtered = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="user-management-page">
      {/* Temp password modal */}
      {tempPasswordData && (
        <TempPasswordModal data={tempPasswordData} onClose={() => setTempPasswordData(null)} />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-heading uppercase tracking-wider text-foreground flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" /> User Management
          </h1>
          <p className="text-xs text-muted-foreground mt-1">{users.length} users on the platform</p>
        </div>
        <button onClick={() => setShowInvite(true)}
          data-testid="invite-user-btn"
          className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-heading uppercase tracking-wider rounded-sm bg-primary/10 text-primary border border-primary/30 hover:bg-primary/15 transition-colors">
          <UserPlus className="w-4 h-4" /> Invite User
        </button>
      </div>

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowInvite(false)}>
          <div className="bg-card border border-border rounded-sm p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-sm font-heading uppercase tracking-wider text-foreground mb-4">Invite New User</h2>
            <form onSubmit={handleInvite} className="space-y-3">
              <input type="text" placeholder="Full name" required value={inviteData.name}
                data-testid="invite-name-input"
                onChange={e => setInviteData(d => ({ ...d, name: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/40" />
              <input type="email" placeholder="Email address" required value={inviteData.email}
                data-testid="invite-email-input"
                onChange={e => setInviteData(d => ({ ...d, email: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/40" />
              <select value={inviteData.role}
                data-testid="invite-role-select"
                onChange={e => setInviteData(d => ({ ...d, role: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-sm text-foreground focus:outline-none focus:border-primary/40">
                <option value="member">Member</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowInvite(false)}
                  className="px-4 py-2 text-xs text-muted-foreground border border-border rounded-sm hover:bg-secondary transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={inviting}
                  data-testid="invite-submit-btn"
                  className="px-4 py-2 text-xs font-heading uppercase tracking-wider bg-primary/15 text-primary border border-primary/30 rounded-sm hover:bg-primary/20 transition-colors disabled:opacity-50">
                  {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <input type="text" placeholder="Search users..." value={search}
          data-testid="user-search-input"
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-sm bg-card border border-border rounded-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/40" />
      </div>

      {/* Role legend */}
      <div className="flex gap-4 mb-4 flex-wrap">
        {Object.entries(ROLE_CONFIG).map(([role, cfg]) => (
          <div key={role} className="flex items-center gap-2">
            <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${cfg.color}`}>{cfg.label}</span>
            <span className="text-[10px] text-muted-foreground">{cfg.desc}</span>
          </div>
        ))}
      </div>

      {/* User table */}
      <div className="border border-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-[1fr_1.5fr_100px_100px_100px_60px] gap-4 px-4 py-2.5 text-[9px] font-heading uppercase tracking-widest text-muted-foreground bg-muted/50 border-b border-border">
          <span>Name</span><span>Email</span><span>Role</span><span>Status</span><span>Joined</span><span></span>
        </div>
        {filtered.map(u => {
          const roleCfg = ROLE_CONFIG[u.role] || ROLE_CONFIG.member;
          const isSelf = u.id === currentUser?.id;
          const hasLoggedIn = !!u.last_login;
          return (
            <div key={u.id}
              data-testid={`user-row-${u.id}`}
              className="grid grid-cols-[1fr_1.5fr_100px_100px_100px_60px] gap-4 px-4 py-3 border-b border-border/50 text-sm items-center hover:bg-muted/30 transition-colors">
              <span className="text-foreground truncate font-medium">
                {u.name} {isSelf && <span className="text-[9px] text-muted-foreground font-mono ml-1">(you)</span>}
              </span>
              <span className="text-muted-foreground truncate font-mono text-xs">{u.email}</span>
              <div className="relative" data-dropdown>
                <button onClick={() => setRoleDropdown(roleDropdown === u.id ? null : u.id)}
                  disabled={isSelf}
                  data-testid={`role-btn-${u.id}`}
                  className={`flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${roleCfg.color} ${isSelf ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}>
                  {roleCfg.label}
                  {!isSelf && <ChevronDown className="w-2.5 h-2.5" />}
                </button>
                {roleDropdown === u.id && (
                  <div className="absolute left-0 top-full mt-1 bg-card border border-border rounded-sm shadow-lg z-20 min-w-[120px]">
                    {Object.entries(ROLE_CONFIG).map(([role, cfg]) => (
                      <button key={role}
                        data-testid={`set-role-${role}-${u.id}`}
                        onClick={() => updateRole(u.id, role)}
                        className={`w-full text-left px-3 py-2 text-[10px] hover:bg-muted/60 transition-colors ${role === u.role ? 'text-primary' : 'text-muted-foreground'}`}>
                        {cfg.label} {role === u.role && '(current)'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className={`text-[9px] font-mono uppercase ${hasLoggedIn ? 'text-emerald-500' : 'text-amber-500'}`}
                data-testid={`user-status-${u.id}`}>
                {hasLoggedIn ? 'Active' : 'Pending'}
              </span>
              <span className="text-[10px] text-muted-foreground font-mono">
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
              </span>
              <div className="relative" data-dropdown>
                {!isSelf && (
                  <>
                    {actionLoading === u.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    ) : (
                      <button onClick={() => setActionsDropdown(actionsDropdown === u.id ? null : u.id)}
                        data-testid={`actions-btn-${u.id}`}
                        className="text-muted-foreground hover:text-foreground/80 transition-colors p-1 rounded-sm hover:bg-muted/50">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    )}
                    {actionsDropdown === u.id && (
                      <div className="absolute right-0 bottom-full mb-1 bg-card border border-border rounded-sm shadow-lg z-20 min-w-[160px]"
                        data-testid={`actions-menu-${u.id}`}>
                        <button
                          onClick={() => resetPassword(u.id)}
                          data-testid={`reset-password-${u.id}`}
                          className="w-full text-left px-3 py-2.5 text-[10px] text-muted-foreground hover:bg-muted/60 hover:text-primary transition-colors flex items-center gap-2">
                          <KeyRound className="w-3 h-3" /> Reset Password
                        </button>
                        {!hasLoggedIn && (
                          <button
                            onClick={() => resendInvite(u.id)}
                            data-testid={`resend-invite-${u.id}`}
                            className="w-full text-left px-3 py-2.5 text-[10px] text-muted-foreground hover:bg-muted/60 hover:text-cyan-400 transition-colors flex items-center gap-2">
                            <Mail className="w-3 h-3" /> Resend Invite
                          </button>
                        )}
                        <button
                          onClick={() => deleteUser(u.id, u.email)}
                          data-testid={`delete-user-${u.id}`}
                          className="w-full text-left px-3 py-2.5 text-[10px] text-muted-foreground hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:text-rose-600 dark:hover:text-rose-400 transition-colors flex items-center gap-2 border-t border-border">
                          <Trash2 className="w-3 h-3" /> Delete User
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">No users found</div>
        )}
      </div>
    </div>
  );
}
